import { normalizeRole } from "@rec/shared";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Building2,
  Check,
  Inbox,
  Loader2,
  Mail,
  Pencil,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  ConfirmInline,
  ErrorState,
  Skeleton,
} from "@/components/ui";
import { AdminAccess } from "@/pages/AdminAccess";
import { cn } from "@/lib/utils";
import { useServerText } from "@/i18n";
import { useFormat, type Formatter } from "@/i18n/format";

/**
 * The platform operator's back-office.
 *
 * Everything here is about running the product rather than using it: approving
 * the companies that asked for access, and then managing those companies and
 * the people in them.
 *
 * It is deliberately separate from the workspace UI. A tenant's super admin is
 * the top of *their* workspace and invites their own colleagues — they never see
 * this screen, and the API enforces that rather than trusting the router.
 */

type Tab = "invite" | "requests" | "tenants" | "people";

// Labels are translated at render (`tabs.<id>` in the admin namespace).
const TABS: Array<{ id: Tab; icon: typeof Inbox }> = [
  { id: "invite", icon: Mail },
  { id: "requests", icon: Inbox },
  { id: "tenants", icon: Building2 },
  { id: "people", icon: Users },
];

export function BackOffice() {
  const { t } = useTranslation(["admin", "common"]);
  const [tab, setTab] = useState<Tab>("invite");

  return (
    <div className="mx-auto max-w-[1000px] space-y-5">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-text">{t("title")}</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          {t("intro")}
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors",
              tab === id
                ? "bg-accent-subtle text-accent"
                : "text-text-muted hover:bg-surface-hover hover:text-text",
            )}
          >
            <Icon size={14} strokeWidth={2} />
            {t(`tabs.${id}`)}
          </button>
        ))}
      </div>

      {tab === "invite" ? <Invitations /> : null}
      {tab === "requests" ? <AdminAccess /> : null}
      {tab === "tenants" ? <Tenants /> : null}
      {tab === "people" ? <People /> : null}
    </div>
  );
}

function formatBytes(bytes: number, f: Formatter): string {
  if (bytes <= 0) return "—";
  return f.bytes(bytes);
}

function formatWhen(iso: string | null, t: TFunction<["admin", "common"]>, f: Formatter): string {
  if (!iso) return t("when.never");
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return t("when.today");
  if (days === 1) return t("when.yesterday");
  if (days < 30) return t("when.daysAgo", { count: days });
  return f.date(iso, { dateStyle: "medium" });
}

function Tenants() {
  const { t } = useTranslation(["admin", "common"]);
  const st = useServerText();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [blocking, setBlocking] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const tenants = useQuery({ queryKey: ["tenants"], queryFn: api.listTenants });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["tenants"] });

  const rename = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.renameTenant(input.id, input.name),
    onSuccess: () => {
      setRenaming(null);
      invalidate();
    },
  });

  const block = useMutation({
    mutationFn: (input: { id: string; blocked: boolean; reason?: string }) =>
      api.blockTenant(input.id, input.blocked, input.reason),
    onSuccess: () => {
      setBlocking(null);
      setReason("");
      invalidate();
    },
  });

  if (tenants.isError) {
    return (
      <ErrorState
        title={t("tenants.loadError")}
        error={tenants.error}
        onRetry={() => void tenants.refetch()}
      />
    );
  }

  if (tenants.isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!tenants.data || tenants.data.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-sm text-text-muted">
          {t("tenants.empty")}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-2.5">
      {tenants.data.map((tenant) => (
        <Card key={tenant.id} className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {renaming === tenant.id ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (draft.trim()) rename.mutate({ id: tenant.id, name: draft.trim() });
                  }}
                >
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                    className="h-8 min-w-0 flex-1 rounded-lg border border-accent bg-bg px-2.5 text-[14px] text-text focus:outline-none"
                  />
                  <Button type="submit" variant="primary" size="sm" disabled={rename.isPending}>
                    {rename.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
                    {t("common:actions.save")}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setRenaming(null)}>
                    {t("common:actions.cancel")}
                  </Button>
                </form>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[15px] font-semibold text-text">{tenant.name}</h3>
                  <span className="font-mono text-[12px] text-text-faint">{tenant.slug}</span>
                  {tenant.blocked ? <Badge tone="danger">{t("suspended")}</Badge> : null}
                  <button
                    type="button"
                    aria-label={t("tenants.renameAria", { name: tenant.name })}
                    title={t("common:actions.rename")}
                    onClick={() => {
                      setDraft(tenant.name);
                      setRenaming(tenant.id);
                    }}
                    className="rounded-md p-1 text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
                  >
                    <Pencil size={12} />
                  </button>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-text-muted">
                <span>
                  <Trans
                    t={t}
                    i18nKey="tenants.people"
                    count={tenant.members}
                    values={{ count: tenant.members }}
                    components={{ num: <span className="tabular-nums text-text" /> }}
                  />
                </span>
                <span>
                  <Trans
                    t={t}
                    i18nKey="tenants.recordings"
                    count={tenant.recordings}
                    values={{ count: tenant.recordings }}
                    components={{ num: <span className="tabular-nums text-text" /> }}
                  />
                </span>
                <span>
                  <Trans
                    t={t}
                    i18nKey="tenants.stored"
                    values={{ size: formatBytes(tenant.storageBytes, f) }}
                    components={{ num: <span className="tabular-nums text-text" /> }}
                  />
                </span>
                <span>
                  {t("tenants.lastRecorded", { when: formatWhen(tenant.lastActivityAt, t, f) })}
                </span>
              </div>

              {tenant.blocked && tenant.blockedReason ? (
                <p className="mt-2 text-[12.5px] text-danger">{st(tenant.blockedReason)}</p>
              ) : null}
            </div>

            <div className="shrink-0">
              {tenant.blocked ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={block.isPending}
                  onClick={() => block.mutate({ id: tenant.id, blocked: false })}
                >
                  <Check size={13} />
                  {t("restore")}
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setBlocking(tenant.id)}>
                  <Ban size={13} />
                  {t("suspend")}
                </Button>
              )}
            </div>
          </div>

          {blocking === tenant.id ? (
            <div className="mt-3.5 rounded-lg border border-danger/40 bg-danger-subtle/40 p-3.5">
              <p className="text-[13px] leading-relaxed text-text">
                <Trans
                  t={t}
                  i18nKey="tenants.suspendQuestion"
                  values={{ name: tenant.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("tenants.reasonPlaceholder")}
                className="mt-2.5 h-9 w-full rounded-lg border border-border bg-bg px-2.5 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
              <div className="mt-2.5 flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={block.isPending}
                  onClick={() =>
                    block.mutate({ id: tenant.id, blocked: true, reason: reason.trim() })
                  }
                >
                  {block.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
                  {t("tenants.suspendWorkspace")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setBlocking(null)}>
                  {t("common:actions.cancel")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function People() {
  const { t } = useTranslation(["admin", "common"]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ["platform-users", search],
    queryFn: () => api.listPlatformUsers(search || undefined),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["platform-users"] });

  const ban = useMutation({
    mutationFn: (input: { id: string; banned: boolean }) =>
      api.banUser(input.id, input.banned),
    onSuccess: () => {
      setConfirming(null);
      invalidate();
    },
  });

  const operator = useMutation({
    mutationFn: (input: { id: string; operator: boolean }) =>
      api.setOperator(input.id, input.operator),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("people.searchPlaceholder")}
        className="h-9 w-full max-w-[320px] rounded-lg border border-border bg-bg px-3 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
      />

      {users.isError ? (
        <ErrorState
          title={t("people.loadError")}
          error={users.error}
          onRetry={() => void users.refetch()}
        />
      ) : users.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !users.data || users.data.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-text-muted">{t("people.empty")}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {users.data.map((user) => (
            <Card key={user.id} className="p-3.5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-medium text-text">
                      {user.name ?? user.email}
                    </span>
                    {user.isOperator ? (
                      <Badge tone="accent">
                        <ShieldCheck size={11} /> {t("people.operator")}
                      </Badge>
                    ) : null}
                    {user.banned ? <Badge tone="danger">{t("suspended")}</Badge> : null}
                  </div>
                  <p className="mt-0.5 text-[12.5px] text-text-muted">{user.email}</p>

                  {user.memberships.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {user.memberships.map((m) => (
                        <span
                          key={m.id}
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-subtle px-2 py-0.5 text-[11.5px] text-text-muted"
                        >
                          {m.name}
                          {/* The stored name ("super_admin") read as jargon; the
                              role's own label ("Owner") is what people know it by. */}
                          <span className="text-text-faint">
                            {t(`common:roles.${normalizeRole(m.role)}`)}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[12px] text-text-faint">
                      {t("people.noWorkspace")}
                    </p>
                  )}
                </div>

                {/*
                  Operators are peers — the API refuses to suspend one, so the
                  control is not offered either. Showing a button that always
                  errors is worse than showing none.
                */}
                {!user.isOperator ? (
                  <div className="flex shrink-0 gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={operator.isPending || user.banned}
                      // Confirmed: this hands somebody every workspace on the
                      // platform, and it was a single click.
                      onClick={() => setPromoting(user.id)}
                      title={t("people.makeOperatorTitle")}
                    >
                      {t("people.makeOperator")}
                    </Button>
                    {user.banned ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={ban.isPending}
                        onClick={() => ban.mutate({ id: user.id, banned: false })}
                      >
                        <Check size={13} />
                        {t("restore")}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setConfirming(user.id)}
                      >
                        <Ban size={13} />
                        {t("suspend")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={operator.isPending}
                    onClick={() => operator.mutate({ id: user.id, operator: false })}
                    title={t("people.removeOperatorTitle")}
                  >
                    <X size={13} />
                    {t("people.removeOperator")}
                  </Button>
                )}
              </div>

              {promoting === user.id ? (
                <div className="mt-3">
                  <ConfirmInline
                    question={
                      <Trans
                        t={t}
                        i18nKey="people.makeOperatorQuestion"
                        values={{ email: user.email }}
                        components={{ strong: <strong /> }}
                      />
                    }
                    confirmLabel={t("people.makeOperator")}
                    pending={operator.isPending}
                    onConfirm={() => {
                      operator.mutate({ id: user.id, operator: true });
                      setPromoting(null);
                    }}
                    onCancel={() => setPromoting(null)}
                  />
                </div>
              ) : null}

              {confirming === user.id ? (
                <div className="mt-3 rounded-lg border border-danger/40 bg-danger-subtle/40 p-3">
                  <p className="text-[13px] leading-relaxed text-text">
                    <Trans
                      t={t}
                      i18nKey="people.suspendQuestion"
                      values={{ email: user.email }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <div className="mt-2.5 flex gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={ban.isPending}
                      onClick={() => ban.mutate({ id: user.id, banned: true })}
                    >
                      {ban.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
                      {t("people.suspendAccount")}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setConfirming(null)}>
                      {t("common:actions.cancel")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Invite somebody to start using the platform.
 *
 * The other way in is an access request, where a stranger asks and you approve.
 * This is the reverse: you already decided, so they get a link and name their
 * own workspace. Neither path lets anyone create a workspace unasked.
 */
function Invitations() {
  const { t } = useTranslation(["admin", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState<string | null>(null);

  const invitations = useQuery({
    queryKey: ["platform-invitations"],
    queryFn: api.listPlatformInvitations,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["platform-invitations"] });

  const send = useMutation({
    mutationFn: () =>
      api.sendPlatformInvitation({
        email: email.trim(),
        name: name.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: (result) => {
      setSent(
        result.resent
          ? t("invitations.resent", { email: email.trim() })
          : t("invitations.sent", { email: email.trim() }),
      );
      setEmail("");
      setName("");
      setNote("");
      setTimeout(() => setSent(null), 8000);
      invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokePlatformInvitation(id),
    onSuccess: invalidate,
  });

  const rows = invitations.data ?? [];

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h2 className="text-[14px] font-semibold text-text">{t("invitations.title")}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
          {t("invitations.body")}
        </p>

        <form
          className="mt-3.5 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) send.mutate();
          }}
        >
          <div className="flex flex-wrap gap-2">
            <label className="min-w-[220px] flex-1">
              <span className="mb-1.5 block text-[12.5px] font-medium text-text">
                {t("invitations.email")}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("invitations.emailPlaceholder")}
                className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
            </label>
            <label className="min-w-[160px] flex-1">
              <span className="mb-1.5 block text-[12.5px] font-medium text-text">
                <Trans
                  t={t}
                  i18nKey="invitations.name"
                  components={{ muted: <span className="font-normal text-text-faint" /> }}
                />
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("invitations.namePlaceholder")}
                className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-text">
              <Trans
                t={t}
                i18nKey="invitations.note"
                components={{ muted: <span className="font-normal text-text-faint" /> }}
              />
            </span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("invitations.notePlaceholder")}
              className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
          </label>

          <Button type="submit" variant="primary" disabled={!email.trim() || send.isPending}>
            {send.isPending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {t("invitations.send")}
          </Button>
        </form>

        {send.error ? (
          <p role="alert" className="mt-2.5 text-[12.5px] text-danger">
            {send.error instanceof Error ? st(send.error.message) : t("invitations.failed")}
          </p>
        ) : null}
        {sent ? <p className="mt-2.5 text-[12.5px] text-success">{sent}</p> : null}
      </Card>

      {rows.length > 0 ? (
        <div className="space-y-2">
          {rows.map((invitation) => (
            <Card key={invitation.id} className="flex flex-wrap items-center justify-between gap-3 p-3.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] text-text">{invitation.email}</span>
                  {invitation.status === "accepted" ? (
                    <Badge tone="success">
                      {invitation.organizationName
                        ? t("invitations.joinedOrg", { organization: invitation.organizationName })
                        : t("invitations.joined")}
                    </Badge>
                  ) : invitation.status === "revoked" ? (
                    <Badge>{t("invitations.revoked")}</Badge>
                  ) : invitation.expired ? (
                    <Badge tone="warning">{t("invitations.expired")}</Badge>
                  ) : (
                    <Badge tone="accent">{t("invitations.waiting")}</Badge>
                  )}
                </div>
                {invitation.note ? (
                  <p className="mt-0.5 text-[12px] text-text-faint">{invitation.note}</p>
                ) : null}
              </div>

              {invitation.status === "pending" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(invitation.id)}
                >
                  {t("revoke")}
                </Button>
              ) : null}
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}
